import React from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen, HelpCircle, Megaphone, Vote, Users, MapPin, X, Command } from "lucide-react";
import { api } from "@/lib/api";

const SECTIONS = [
  { key: "articles",      label: "Articles",      Icon: BookOpen,  buildHref: (it) => `/actualites/${it.slug}` },
  { key: "consultations", label: "Consultations", Icon: Vote,      buildHref: (it) => `/donner-mon-avis/${it.slug}` },
  { key: "actions",       label: "Actions",       Icon: Megaphone, buildHref: (it) => `/actions/${it.id}` },
  { key: "initiatives",   label: "Sur la carte",  Icon: MapPin,    buildHref: () => `/carte` },
  { key: "groups",        label: "Groupes locaux", Icon: Users,    buildHref: () => `/carte#groupes` },
  { key: "faq",           label: "FAQ",           Icon: HelpCircle, buildHref: () => `/comprendre?tab=droits` },
];

/**
 * Global search palette opened by Ctrl+K / ⌘+K (also via the search icon in the header).
 * Hits /api/search and groups results by section.
 */
export default function SearchPalette() {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef(null);
  const navigate = useNavigate();

  // Global keyboard shortcut: Ctrl+K / ⌘+K
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults(null); }
  }, [open]);

  // Debounced fetch
  React.useEffect(() => {
    if (!open || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q, limit: 6 } });
        setResults(data);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const totalResults = results ? SECTIONS.reduce((n, s) => n + (results[s.key]?.length || 0), 0) : 0;

  const go = (href) => { setOpen(false); navigate(href); };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center w-10 h-10 border-2 border-black rounded-md bg-white hover:bg-cef-yellow transition shadow-[2px_2px_0_rgba(0,0,0,1)] hover:shadow-[3px_3px_0_rgba(0,0,0,1)] hover:-translate-y-0.5"
        aria-label="Recherche globale (Ctrl+K)"
        data-testid="open-search-btn"
        title="Rechercher (Ctrl+K)"
      >
        <Search className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-20 px-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          data-testid="search-palette"
        >
          <div
            className="w-full max-w-2xl bg-white border-2 border-black rounded-md shadow-brutal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-black">
              <Search className="w-5 h-5" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher articles, FAQ, actions, groupes…"
                className="flex-1 outline-none text-base bg-transparent"
                data-testid="search-input"
              />
              {loading && <span className="text-xs text-neutral-500 animate-pulse">…</span>}
              <button onClick={() => setOpen(false)} className="text-neutral-500 hover:text-black" aria-label="Fermer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {!q && (
                <div className="p-4 text-sm text-neutral-500 flex items-center gap-2">
                  <Command className="w-4 h-4" /> Tape pour rechercher dans tout le site
                </div>
              )}
              {q && q.trim().length < 2 && (
                <div className="p-4 text-sm text-neutral-500">Tape au moins 2 caractères…</div>
              )}
              {q.trim().length >= 2 && results && totalResults === 0 && !loading && (
                <div className="p-4 text-sm text-neutral-500" data-testid="search-empty">
                  Aucun résultat pour « {q} ».
                </div>
              )}
              {q.trim().length >= 2 && results && totalResults > 0 && (
                <div className="grid gap-3">
                  {SECTIONS.map(({ key, label, Icon, buildHref }) => {
                    const items = results[key] || [];
                    if (!items.length) return null;
                    return (
                      <div key={key} data-testid={`search-section-${key}`}>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 px-2 mb-1 flex items-center gap-1">
                          <Icon className="w-3 h-3" /> {label} <span className="opacity-50">· {items.length}</span>
                        </div>
                        <div className="grid">
                          {items.map((it) => (
                            <button
                              key={it.id}
                              onClick={() => go(buildHref(it))}
                              className="text-left px-3 py-2 rounded-md hover:bg-cef-yellow transition border-b border-black/5"
                              data-testid={`search-item-${key}-${it.id}`}
                            >
                              <div className="text-sm font-bold">{it.title || it.name || it.question}</div>
                              <div className="text-xs text-neutral-500 mt-0.5">
                                {it.category && <span>#{it.category} · </span>}
                                {it.city && <span>{it.city}</span>}
                                {it.province && <span> · {it.province}</span>}
                                {it.type && <span> · {it.type}</span>}
                                {it.excerpt && <span className="line-clamp-1">{it.excerpt}</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t-2 border-black px-3 py-2 text-[10px] font-mono text-neutral-500 flex justify-between">
              <span>Échap pour fermer</span>
              <span>Ctrl+K pour ouvrir</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
